import { FC, useState, MouseEvent, ChangeEvent, useEffect } from 'react';
import Cookies from 'js-cookie';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import {
  actionButtons,
  apiUrls,
  confirmMessages,
  dataStorageKeywords,
  help,
  errorMessages,
  purchaseOrderDialogTitles,
  purchaseOrderDialogSubtitles,
  purchaseOrderFormTitles,
  pagination,
} from 'src/utils/properties.utils';
import { getEncryptedLocalStorageData } from 'src/utils/dataStorageManagement.utils';
import {
  getPurchaseOrderPermissions,
  PurchaseOrderPermissions,
} from 'src/utils/purchaseOrderPermissions.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { capitalizeFirstLetter } from 'src/utils/text.utils';
import {
  PurchaseOrder,
  DEFAULT_PURCHASE_ORDER,
  PurchaseOrderItemResponse,
  PurchaseOrderDisplayItem,
  PurchaseOrderFormItem,
  PurchaseOrderDynamicFieldValues,
} from 'src/models/Management.interface';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useFormik } from 'formik';
import { purchaseOrderInitialValues } from 'src/utils/initialValues.utils';
import { purchaseOrderValidation } from 'src/utils/validationScheme.utils';
import { ApprovedItem } from 'src/models/DialogBox.interface';
import ViewDialog from 'src/components/DialogBox/POBox/ViewBox';
import TextField from 'src/components/InputFields/TextField';
import PurchaseOrderDialog from 'src/components/DialogBox/POBox/OrderBox';
import RawMaterialSelect from 'src/components/InputFields/RawMaterialPicker';
import TextArea from 'src/components/InputFields/TextArea';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  calculatePurchaseOrderDialogHeight,
  isPurchaseOrderStatus,
} from 'src/utils/purchaseOrderActionMenu.utils';
import { PurchaseOrderStatus } from 'src/models/enums/PurchaseOrderStatus.enum';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
  EllipsisText40Instructions,
  EllipsisText40PrimaryNavy,
  EllipsisText30Instructions,
  EllipsisText30PrimaryNavy,
  SaveButton,
  CancelButton,
  MinusIconBox,
  PlusIconBox,
  EllipsisTextPrimaryNavy,
  ItemsContainerBox,
  ItemsHeaderBox,
  ItemRowBox,
  SectionTitleTypography,
  SectionTitleTypographyWithMargin,
  FormSectionBox,
  FormRowBox,
  FormColumnBox,
  ItemDetailsContainer,
  QuantityInputContainer,
  RemoveItemContainer,
  AddItemTypography,
  ButtonContainer,
  AddItemContainer,
  BoxWithMarginBottom2,
  FlexColumnBox,
  BoxItemTextField,
  ScrollableContentBox,
  ButtonContainerBottom,
  RightAlignedTextField,
} from 'src/components/StyledComponents/StyledActionMenu';
import FormikSelectField, { Option } from '../InputFields/FormikSelectField';
import { useGetVendor } from 'src/services/vendor.service';
import { BorderedSectionBox } from '../DialogBox/GRNPaymentDialog/GRNPaymentDialog.styles';
import { getBasicMaterials } from 'src/services/material.service';
import { generatePurchaseOrderPDF } from 'src/components/Invoice/PurchaseOrderPDF';

const PurchaseOrderActionMenuButton: FC<{
  rowId: string;
  id: string;
  status: PurchaseOrderStatus;
  fetchPurchaseOrders: () => void;
  deletePurchaseOrder: (id: string) => void;
}> = ({ rowId, id, status, fetchPurchaseOrders, deletePurchaseOrder }) => {
  const { showSnackbar } = StyledSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<string | null>(null);
  const [orderStatus, setOrderStatus] = useState<PurchaseOrderStatus>(status);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingSaveComplete, setLoadingSaveComplete] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(
    null
  );
  const [newStatus, setNewStatus] = useState<PurchaseOrderStatus>(status);
  const [initialStatus, setInitialStatus] =
    useState<PurchaseOrderStatus>(status);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [initialReason] = useState<string | null>(rejectionReason);
  const [touched, setTouched] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { basicVendors, fetchBasicVendors } = useGetVendor();
  const [permissions, setPermissions] = useState<PurchaseOrderPermissions>({
    canView: false,
    canEdit: false,
    canUpdateStatus: false,
    canDelete: false,
    canCreateGRN: false,
    canPrint: false,
  });
  const [items, setItems] = useState<
    { id: string; rawMaterialId: string; requestedQty: string }[]
  >([{ id: Date.now().toString(), rawMaterialId: '', requestedQty: '' }]);
  const [rawMaterials, setRawMaterials] = useState<
    { id: string; name: string }[]
  >([]);

  // State to store inactive vendor when editing a purchase order
  const [inactiveVendor, setInactiveVendor] = useState<{
    id: string;
    organizationName: string;
  } | null>(null);

  // Build vendor options including inactive vendor if present
  const vendorOptions: Option[] = [
    ...basicVendors.map((vendor) => ({
      label: vendor.organizationName,
      value: vendor.id,
    })),
    ...(inactiveVendor && !basicVendors.some((v) => v.id === inactiveVendor.id)
      ? [
          {
            label: inactiveVendor.organizationName,
            value: inactiveVendor.id,
            disabled: true,
          },
        ]
      : []),
  ];

  useEffect(() => {
    const fetchRole = async () => {
      const role = await getEncryptedLocalStorageData(
        dataStorageKeywords.HIGHEST_ROLE
      );
      setPermissions(getPurchaseOrderPermissions(role || '', status));
    };
    fetchRole();
  }, []);
  const hasAnyAction = () => {
    return (
      permissions.canView ||
      permissions.canEdit ||
      permissions.canUpdateStatus ||
      permissions.canDelete ||
      permissions.canCreateGRN ||
      permissions.canPrint
    );
  };

  const handleReasonChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setTouched(true);
    setRejectionReason(e.target.value);
  };

  const validateReason = () =>
    rejectionReason.trim().length === 0 && touched
      ? 'Rejection note is required'
      : null;

  const calculateDialogHeight = () =>
    calculatePurchaseOrderDialogHeight(formik);

  const handleEditStatusChange = async (status: PurchaseOrderStatus) => {
    setOrderStatus(status);

    if (status === PurchaseOrderStatus.APPROVED) {
      const updatedItems = formik.values.purchaseOrderItems.map(
        (item: PurchaseOrderDisplayItem) => ({
          ...item,
          approvedQty: item.approvedQty || Number(item.requestedQty),
        })
      );
      formik.setFieldValue('purchaseOrderItems', updatedItems);
    }

    setTimeout(async () => {
      const errors = await formik.validateForm();

      if (Object.keys(errors).length === 0) {
        formik.handleSubmit();
      } else {
        formik.setTouched(
          Object.keys(formik.values).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {}
          )
        );
      }
    }, 0);
  };

  const handleStatusChange = (updatedStatus: PurchaseOrderStatus) => {
    setNewStatus(updatedStatus);

    if (updatedStatus === PurchaseOrderStatus.APPROVED) {
      const updatedItems = formik.values.purchaseOrderItems.map(
        (item: PurchaseOrderDisplayItem) => ({
          ...item,
          approvedQty: item.approvedQty || Number(item.requestedQty),
        })
      );

      formik.setFieldValue('purchaseOrderItems', updatedItems);
    }

    if (updatedStatus === PurchaseOrderStatus.REJECTED) {
      setTouched(true);
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleClick = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleEditDialogOpen = async (order: PurchaseOrder) => {
    setSelectedOrder(order);
    await fetchBasicVendors();

    // Load raw materials once for all items
    if (rawMaterials.length === 0) {
      try {
        const materials = await getBasicMaterials();
        setRawMaterials(materials);
      } catch (error) {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    }

    try {
      const response = await axiosTokenInstance.get(
        apiUrls.GET_PURCHASE_ORDERS_BY_ID,
        { params: { id: order.id } }
      );

      const purchaseOrder = response.data;

      const loadedItems = purchaseOrder.purchaseItems.map(
        (item: PurchaseOrderItemResponse) => ({
          id: item.id,
          rawMaterialId: item.rawMaterialId,
          requestedQty: item.requestedQty.toString(),
        })
      );
      setItems(loadedItems);

      // Check if vendor is inactive and store it for the dropdown
      if (purchaseOrder.vendor && purchaseOrder.vendor.isActive === false) {
        setInactiveVendor({
          id: purchaseOrder.vendor.id,
          organizationName: purchaseOrder.vendor.organizationName,
        });
      } else {
        setInactiveVendor(null);
      }

      const dynamicFields: Record<string, string> = {};
      loadedItems.forEach((item: PurchaseOrderFormItem) => {
        dynamicFields[`rawMaterialId-${item.id}`] = item.rawMaterialId;
        dynamicFields[`requestedQty-${item.id}`] = item.requestedQty;
      });

      formik.setValues({
        id: purchaseOrder.id,
        createdAt: new Date(purchaseOrder.createdAt).toLocaleDateString(
          'en-GB'
        ),
        vendor: purchaseOrder.vendor.id,
        deliveryAddress: purchaseOrder.deliveryAddress,
        status: purchaseOrder.status,
        preparedBy: purchaseOrder.preparedBy || '',
        approvedBy: purchaseOrder.approvedBy || '',
        approvedDate: purchaseOrder.approvedDate || null,
        notes: purchaseOrder.notes || '',
        purchaseOrderItems: loadedItems,
        ...dynamicFields,
      });
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
    setEditDialogOpen(true);
    handleClose();
  };

  const handleEditFormSubmit = async () => {
    if (!selectedOrder) {
      return;
    }

    // Set loading based on order status (which button was clicked)
    if (orderStatus === PurchaseOrderStatus.NEW) {
      setLoadingSave(true);
    } else if (orderStatus === PurchaseOrderStatus.PENDING) {
      setLoadingSaveComplete(true);
    }

    try {
      const purchaseItems = items.map((item) => ({
        id: item.id,
        rawMaterialId: (
          formik.values as unknown as PurchaseOrderDynamicFieldValues
        )[`rawMaterialId-${item.id}`],
        requestedQty: Number(
          (formik.values as unknown as PurchaseOrderDynamicFieldValues)[
            `requestedQty-${item.id}`
          ] || item.requestedQty
        ),
        approvedQty: 0,
      }));
      const response = await axiosTokenInstance.put(
        apiUrls.EDIT_PURCHASE_ORDERS,
        {
          id: selectedOrder.id,
          vendorId: formik.values.vendor,
          deliveryAddress: formik.values.deliveryAddress,
          purchaseItems: purchaseItems,
          status: orderStatus,
        }
      );

      setEditDialogOpen(false);
      fetchPurchaseOrders();
      showSnackbar(response.data.message, 'success');
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoadingSave(false);
      setLoadingSaveComplete(false);
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedOrder(null);
    setInactiveVendor(null);
    setItems([
      { id: Date.now().toString(), rawMaterialId: '', requestedQty: '' },
    ]);
    setLoadingSave(false);
    setLoadingSaveComplete(false);
    formik.resetForm();
  };

  const handleDialogOpen = async (type: string) => {
    setDialogType(type);
    setDialogOpen(true);
    handleClose();
  };

  const handleConfirm = async (status: string, reason: string) => {
    try {
      if (dialogType === actionButtons.DELETE) {
        if (id) {
          await deletePurchaseOrder(id);
        }
        fetchPurchaseOrders();
      }
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDialogOpen(false);
      setDialogType(null);
      setSelectedOrder(null);
    }
  };

  const handleUpdateDialogOpen = async (order: PurchaseOrder) => {
    setSelectedOrder(order);
    try {
      const response = await axiosTokenInstance.get(
        apiUrls.GET_PURCHASE_ORDERS_BY_ID,
        { params: { id: order.id } }
      );

      const purchaseOrder = response.data;

      const purchaseOrderItems = purchaseOrder.purchaseItems.map(
        (item: PurchaseOrderItemResponse) => ({
          rawMaterialId: item.rawMaterialId,
          name: item.name || '',
          requestedQty: item.requestedQty.toString(),
          approvedQty: item.approvedQty,
          id: item.id,
        })
      );

      formik.setValues({
        id: purchaseOrder.id,
        createdAt: new Date(purchaseOrder.createdAt).toLocaleDateString(
          'en-GB'
        ),
        vendor: purchaseOrder.vendor.organizationName,
        deliveryAddress: purchaseOrder.deliveryAddress,
        status: purchaseOrder.status,
        preparedBy: purchaseOrder.preparedBy,
        approvedBy: purchaseOrder.approvedBy,
        approvedDate: purchaseOrder.approvedDate
          ? new Date(purchaseOrder.approvedDate).toLocaleDateString('en-GB')
          : null,
        notes: purchaseOrder.notes,
        purchaseOrderItems: purchaseOrderItems,
      });

      setRejectionReason(response.data.rejectionReason || '');
      setNewStatus(purchaseOrder.status);
      setInitialStatus(purchaseOrder.status);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
    setUpdateDialogOpen(true);
    handleClose();
  };

  const handleUpdateStatus = async (
    status: PurchaseOrderStatus,
    reason: string,
    approvedItems: ApprovedItem[]
  ) => {
    for (const item of approvedItems) {
      if (!Number.isInteger(item.approvedQty)) {
        showSnackbar(errorMessages.APPROVED_QUANTITY_MUST_BE_INTEGER, 'error');
        return;
      }
    }

    try {
      const response = await axiosTokenInstance.patch(
        apiUrls.TOGGLE_PURCHASE_ORDER_STATUS,
        {
          id: selectedOrder?.id,
          status: status,
          approvedDate: new Date().toISOString(),
          approvedBy: Cookies.get(dataStorageKeywords.USER_ID),
          notes: status === PurchaseOrderStatus.REJECTED ? reason : null,
          approvedItems:
            status === PurchaseOrderStatus.APPROVED ? approvedItems : [],
        }
      );
      showSnackbar(response.data.message, 'success');
      fetchPurchaseOrders();
      handleUpdateDialogClose();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setUpdateDialogOpen(false);
      setSelectedOrder(null);
    }
  };

  const handleUpdateDialogClose = () => {
    setUpdateDialogOpen(false);
    setSelectedOrder(null);
    setNewStatus(PurchaseOrderStatus.PENDING);
    setTouched(false);
    formik.resetForm();
  };

  const handleViewDialogOpen = async (order: PurchaseOrder) => {
    setSelectedOrder(order);
    try {
      const response = await axiosTokenInstance.get(
        apiUrls.GET_PURCHASE_ORDERS_BY_ID,
        { params: { id: order.id } }
      );

      formik.setValues({
        id: response.data.id,
        createdAt: new Date(response.data.createdAt).toLocaleDateString(
          'en-GB'
        ),
        vendor: response.data.vendor.organizationName,
        deliveryAddress: response.data.deliveryAddress,
        status: response.data.status,
        preparedBy: response.data.preparedBy,
        approvedBy: response.data.approvedBy,
        approvedDate: response.data.approvedDate
          ? new Date(response.data.approvedDate).toLocaleDateString('en-GB')
          : null,
        notes: response.data.notes !== null ? response.data.notes : null,
        purchaseOrderItems: response.data.purchaseItems.map(
          (item: PurchaseOrderItemResponse) => ({
            rawMaterialId: item.rawMaterialId,
            name: item.name,
            requestedQty: item.requestedQty.toString(),
            approvedQty: item.approvedQty.toString(),
            id: item.id,
          })
        ),
      });
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
    setViewDialogOpen(true);
    handleClose();
  };

  const handleViewDialogClose = () => {
    setViewDialogOpen(false);
    setSelectedOrder(null);
    formik.resetForm();
  };

  const handleAddItem = () => {
    const newItem = {
      id: Date.now().toString(),
      rawMaterialId: '',
      requestedQty: '',
    };
    setItems([...items, newItem]);

    // Preserve existing form values and add new fields for the new item
    formik.setValues({
      ...formik.values,
      [`rawMaterialId-${newItem.id}`]: '',
      [`requestedQty-${newItem.id}`]: '',
    });
  };

  const handleRemoveItem = (itemId: string) => {
    setItems(items.filter((item) => item.id !== itemId));

    const newValues = { ...formik.values };
    delete (newValues as Record<string, unknown>)[`rawMaterialId-${itemId}`];
    delete (newValues as Record<string, unknown>)[`requestedQty-${itemId}`];
    formik.setValues(newValues);
  };

  const handleDialogBoxHeight = (status: string) => {
    if (status === PurchaseOrderStatus.APPROVED) {
      return '620px';
    } else {
      return '530px';
    }
  };

  const formik = useFormik({
    initialValues: purchaseOrderInitialValues,
    validationSchema: purchaseOrderValidation(items),
    enableReinitialize: false,
    onSubmit: handleEditFormSubmit,
  });

  const selectedMaterialIds = items
    .map(
      (item) =>
        (formik.values as unknown as PurchaseOrderDynamicFieldValues)[
          `rawMaterialId-${item.id}`
        ]
    )
    .filter(Boolean);

  const handlePrint = async () => {
    handleClose();
    try {
      await generatePurchaseOrderPDF(rowId);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  return (
    <>
      {hasAnyAction() && (
        <ActionMenuButton
          onClick={handleClick}
          component="i"
          className="bx bx-dots-horizontal-rounded"
        />
      )}
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          {permissions.canView && (
            <MenuItemWithIcon
              onClick={() =>
                handleViewDialogOpen({
                  ...DEFAULT_PURCHASE_ORDER,
                  id: rowId,
                })
              }
              iconClassName="bx bx-file-find"
              text="View"
            />
          )}

          {permissions.canEdit && (
            <MenuItemWithIcon
              onClick={() =>
                handleEditDialogOpen({
                  ...DEFAULT_PURCHASE_ORDER,
                  id: rowId,
                })
              }
              iconClassName="bx bx-edit"
              text="Edit"
            />
          )}
          {permissions.canUpdateStatus && (
            <MenuItemWithIcon
              onClick={() =>
                handleUpdateDialogOpen({
                  ...DEFAULT_PURCHASE_ORDER,
                  id: rowId,
                })
              }
              iconClassName="bx bx-refresh"
              text="Approve / Reject"
            />
          )}

          {permissions.canCreateGRN && (
            <MenuItemWithIcon
              onClick={() => {
                // TODO: Implement Create GRN functionality
              }}
              iconClassName="bx bx-add-to-queue"
              text="Create GRN"
            />
          )}

          {permissions.canDelete && (
            <MenuItemWithIcon
              onClick={() => handleDialogOpen(actionButtons.DELETE)}
              iconClassName="bx bx-trash"
              text="Delete"
            />
          )}

          <MenuItemWithIcon
            onClick={handlePrint}
            iconClassName="bx bx-printer"
            text="Print"
          />
        </MenuItemsWrapper>
      </ActionMenuPopover>

      <ConfirmationDialog
        height="275px"
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        title={'Delete Purchase Order'}
        subtitle={confirmMessages.CONFIRM_DELETE_PURCHASE_ORDER}
        confirmText={capitalizeFirstLetter(dialogType || '')}
        confirmButtonColor={'var(--error)'}
        alertIconColor={'var(--error)'}
      />
      <ViewDialog
        type="purchase"
        height={handleDialogBoxHeight(formik.values.status)}
        width="730px"
        open={viewDialogOpen}
        handleClose={handleViewDialogClose}
        title={purchaseOrderDialogTitles.VIEW}
        vendor={formik.values.vendor}
        preparedBy={formik.values.preparedBy}
        date={formik.values.createdAt}
        address={formik.values.deliveryAddress}
        status={formik.values.status}
        approvedBy={formik.values.approvedBy}
        approvedDate={formik.values.approvedDate || undefined}
        reason={formik.values.notes || undefined}
        subtitle={purchaseOrderDialogSubtitles.VIEW}
        onSubmit={() => handleViewDialogClose()}
        smScreen={false}
        hideButton={true}
        rightAligned={true}
      >
        {selectedOrder && (
          <>
            <BoxWithMarginBottom2>
              <SectionTitleTypography>
                {purchaseOrderFormTitles.ITEMS}
              </SectionTitleTypography>
              <ItemsContainerBox width="100%">
                <ItemsHeaderBox>
                  <EllipsisText40Instructions>Item</EllipsisText40Instructions>
                  <EllipsisText30Instructions>
                    Requested Quantity
                  </EllipsisText30Instructions>
                  <EllipsisText30Instructions>
                    Approved Quantity
                  </EllipsisText30Instructions>
                </ItemsHeaderBox>

                {formik.values.purchaseOrderItems &&
                  formik.values.purchaseOrderItems.map(
                    (item: PurchaseOrderDisplayItem) => (
                      <ItemRowBox key={item.id}>
                        <EllipsisText40PrimaryNavy>
                          {item.name}
                        </EllipsisText40PrimaryNavy>

                        <EllipsisText30PrimaryNavy>
                          {item.requestedQty}
                        </EllipsisText30PrimaryNavy>

                        <EllipsisText30PrimaryNavy>
                          {isPurchaseOrderStatus(
                            status,
                            PurchaseOrderStatus.APPROVED
                          )
                            ? item.approvedQty
                            : '-'}
                        </EllipsisText30PrimaryNavy>
                      </ItemRowBox>
                    )
                  )}
              </ItemsContainerBox>
            </BoxWithMarginBottom2>
          </>
        )}
      </ViewDialog>
      <PurchaseOrderDialog
        height={`${calculateDialogHeight()}px`}
        open={editDialogOpen}
        handleClose={handleEditDialogClose}
        buttonLabel="Save"
        title={purchaseOrderDialogTitles.EDIT}
        subtitle={purchaseOrderDialogSubtitles.EDIT}
        formTitle={purchaseOrderFormTitles.ORDER_DETAILS}
        onSubmit={formik.handleSubmit}
        button1Loading={loadingSave}
        button2Loading={loadingSaveComplete}
        buttonLabel2={
          isPurchaseOrderStatus(formik.values.status, PurchaseOrderStatus.NEW)
            ? 'Save and Complete'
            : undefined
        }
        onButtonClick={handleEditStatusChange}
      >
        <FlexColumnBox>
          <FormRowBox>
            <FormColumnBox>
              <FormikSelectField
                id="vendor"
                label="vendor"
                formikProps={formik}
                options={vendorOptions}
                width="300px"
              />
            </FormColumnBox>

            <TextArea
              color="var(--subtext)"
              id="deliveryAddress"
              label="Delivery Address"
              formikProps={formik}
              rows={5}
              height="140px"
              overflowY="hidden"
            />
          </FormRowBox>
          <BorderedSectionBox>
            <SectionTitleTypography>Item Details</SectionTitleTypography>

            {items.map((item, index) => (
              <ItemDetailsContainer key={item.id}>
                <FormColumnBox>
                  <RawMaterialSelect
                    showLabel={index === 0}
                    color="var(--)"
                    width="300px"
                    id={`rawMaterialId-${item.id}`}
                    label="Item"
                    formikProps={formik}
                    selectedIds={selectedMaterialIds}
                    rawMaterialsList={rawMaterials}
                  />
                </FormColumnBox>
                <QuantityInputContainer>
                  <TextField
                    showLabel={index === 0}
                    color="var(--subtext)"
                    type="number"
                    width="300px"
                    id={`requestedQty-${item.id}`}
                    label="Requested Quantity"
                    formikProps={formik}
                  />
                </QuantityInputContainer>
                {items.length > 1 && (
                  <RemoveItemContainer>
                    <MinusIconBox
                      component="i"
                      className="bx bx-minus-circle 1"
                      onClick={() => handleRemoveItem(item.id)}
                    />
                  </RemoveItemContainer>
                )}
              </ItemDetailsContainer>
            ))}
          </BorderedSectionBox>
          <AddItemContainer onClick={handleAddItem}>
            <PlusIconBox component="i" className="bx bx-plus" />
            <AddItemTypography>Add Item</AddItemTypography>
          </AddItemContainer>
        </FlexColumnBox>
      </PurchaseOrderDialog>
      <ViewDialog
        type="status"
        width="676px"
        height="530px"
        open={updateDialogOpen}
        handleClose={handleUpdateDialogClose}
        title={purchaseOrderDialogTitles.UPDATE_STATUS}
        vendor={formik.values.vendor}
        preparedBy={formik.values.preparedBy}
        date={formik.values.createdAt}
        address={formik.values.deliveryAddress}
        approvedBy={formik.values.approvedBy}
        approvedDate={formik.values.approvedDate || undefined}
        subtitle={purchaseOrderDialogSubtitles.UPDATE_STATUS}
        onConfirm={handleUpdateStatus}
        smScreen={false}
        hideButton={true}
        status={newStatus}
        onStatusChange={handleStatusChange}
        statusOptions={[
          PurchaseOrderStatus.APPROVED,
          PurchaseOrderStatus.REJECTED,
        ]}
      >
        {selectedOrder && (
          <>
            <BoxWithMarginBottom2>
              {isPurchaseOrderStatus(
                newStatus,
                PurchaseOrderStatus.REJECTED
              ) && (
                <TextArea
                  width="604px"
                  height="85px"
                  rows={3}
                  id="reason"
                  label="Rejection Note"
                  fontSize="14px"
                  color="var(--instructions)"
                  value={rejectionReason}
                  onChange={handleReasonChange}
                  errorState={touched && rejectionReason.trim().length === 0}
                  customValidation={() =>
                    touched && rejectionReason.trim().length === 0
                      ? 'Rejection note is required'
                      : null
                  }
                />
              )}

              <SectionTitleTypography>
                {purchaseOrderFormTitles.ITEMS}
              </SectionTitleTypography>
              <ScrollableContentBox height="275px" width="615px">
                <ItemsContainerBox height="auto" width="93%" noScroll>
                  <ItemsHeaderBox>
                    <EllipsisText40Instructions>
                      Item
                    </EllipsisText40Instructions>
                    <EllipsisText30Instructions>
                      Requested Quantity
                    </EllipsisText30Instructions>
                    <EllipsisText30Instructions>
                      Approved Quantity
                    </EllipsisText30Instructions>
                  </ItemsHeaderBox>

                  {formik.values.purchaseOrderItems &&
                    formik.values.purchaseOrderItems.map(
                      (item: PurchaseOrderDisplayItem, index: number) => (
                        <ItemRowBox key={item.id}>
                          <EllipsisText40PrimaryNavy>
                            {item.name}
                          </EllipsisText40PrimaryNavy>

                          <EllipsisText30PrimaryNavy>
                            {item.requestedQty}
                          </EllipsisText30PrimaryNavy>

                          <BoxItemTextField>
                            {isPurchaseOrderStatus(
                              newStatus,
                              PurchaseOrderStatus.APPROVED
                            ) ? (
                              <RightAlignedTextField
                                id={`purchaseOrderItems.${index}.approvedQty`}
                                label=""
                                type="number"
                                showLabel={false}
                                formikProps={formik}
                                value={item.approvedQty}
                                onChange={(e) => {
                                  const value = e.target.value.replace(
                                    /,/g,
                                    ''
                                  );
                                  if (value === '' || /^\d+$/.test(value)) {
                                    formik.setFieldValue(
                                      `purchaseOrderItems.${index}.approvedQty`,
                                      value === '' || value === '0'
                                        ? 1
                                        : Number(value)
                                    );
                                  }
                                }}
                                width="100%"
                                height="40px"
                              />
                            ) : (
                              <EllipsisTextPrimaryNavy>
                                -
                              </EllipsisTextPrimaryNavy>
                            )}
                          </BoxItemTextField>
                        </ItemRowBox>
                      )
                    )}
                </ItemsContainerBox>
                <ButtonContainerBottom>
                  <SaveButton
                    disabled={
                      newStatus === initialStatus ||
                      (isPurchaseOrderStatus(
                        newStatus,
                        PurchaseOrderStatus.REJECTED
                      ) &&
                        rejectionReason.trim().length === 0)
                    }
                    onClick={() => {
                      if (
                        isPurchaseOrderStatus(
                          newStatus,
                          PurchaseOrderStatus.REJECTED
                        ) &&
                        rejectionReason.trim().length === 0
                      ) {
                        setTouched(true);
                        return;
                      }

                      handleUpdateStatus(
                        newStatus,
                        rejectionReason,
                        formik.values.purchaseOrderItems?.map(
                          (item: PurchaseOrderDisplayItem) => ({
                            purchaseOrderItemId: item.id.toString(),
                            approvedQty: Number(item.approvedQty),
                          })
                        ) || []
                      );
                    }}
                    variant="contained"
                  >
                    Save
                  </SaveButton>

                  <CancelButton
                    onClick={handleUpdateDialogClose}
                    className="btn-inactive"
                  >
                    Cancel
                  </CancelButton>
                </ButtonContainerBottom>
              </ScrollableContentBox>
            </BoxWithMarginBottom2>
          </>
        )}
      </ViewDialog>
    </>
  );
};
export default PurchaseOrderActionMenuButton;
